import { ReactNode } from 'react';

/**
 * 강사 앱 지시 카드 — **한 장**.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 컴포넌트로 뺐나
 * ─────────────────────────────────────────────────────────────
 * 원장 웹에는 "강사에게 이렇게 보입니다"라고 말하는 자리가 두 곳입니다.
 *   · 보내기 전  — 확인 창의 미리보기 (CoachOrderPreview)
 *   · 보낸 뒤    — 진행 현황 옆의 강사 앱 미리보기 (CoachTaskMobilePreview)
 * 두 곳이 각자 카드를 그리면 언젠가 갈립니다. 실제로 갈려 있었습니다 —
 * 한쪽은 「원장님 메모」, 실제 앱은 「관리자님 메모」였고, 글의 순서도 달랐습니다.
 * 미리보기가 실제와 다르면 그건 미리보기가 아니라 오해입니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 순서와 이름표는 **앱이 원본**입니다
 * ─────────────────────────────────────────────────────────────
 * rappoApp `src/screens/coach/CoachOrder/ModalCoachOpsOrder.tsx` 를 그대로 따릅니다.
 *
 *   회원 이름 → 왜(reason) → 근거(facts) → ─── → 무엇을(task)
 *   → 「관리자님 메모」(ownerNote, 따옴표) → 「관리자님이 적어준 문구」(memberDraft)
 *
 * 앱 화면이 바뀌면 여기도 바뀌어야 합니다. 이름표를 마음대로 고치지 마세요 —
 * 원장님은 여기서 본 모양을 믿고 보냅니다.
 *
 * 이 카드는 **그리기만 합니다.** 누를 것은 부르는 쪽이 footer 로 넣습니다.
 * 보내기 전 미리보기의 버튼은 장식이고, 보낸 뒤 미리보기의 버튼은 흉내입니다 —
 * 둘 다 아무것도 저장하지 않지만 성격이 달라 여기서 합치지 않습니다.
 */

export type CoachOrderFact = {
    label: string;
    value: string;
};

type Props = {
    memberName: string;
    /**
     * 왜 이 회원인가. 브리핑 항목의 `recommendation.reason` 이 그대로 굳습니다
     * (서버 loadSourceSnapshot → reasonSnapshot). 회원 현황에서 바로 낸 지시에는
     * 없습니다 — 서버가 없는 근거를 지어내지 않기 때문입니다.
     */
    reason?: string | null;
    /** 근거 수치. 위와 같은 이유로 회원 현황 경로에서는 빈 배열입니다 */
    facts?: CoachOrderFact[];
    task: string;
    /** 원장이 **강사에게** 하는 말. 있을 때만 상자가 생깁니다 */
    ownerNote?: string | null;
    /** 회원에게 건넬 문구. 없으면 draftFallback 이 그 자리를 대신합니다 */
    memberDraft?: string | null;
    /**
     * 문구가 없을 때 그 **자리에** 적을 말.
     *
     * 앱이 만드는 문장을 여기서 흉내 내지 않습니다. 앱은 회원 이름·잔여·만료로
     * 문장을 만드는데(rappoApp features/opsOrder/draft.ts), 웹이 비슷한 문장을
     * 지어내면 원장이 본 문장과 강사가 받는 문장이 갈립니다.
     */
    draftFallback: ReactNode;
    /** 카드 맨 위 (요청/완료 배지 등) */
    badge?: ReactNode;
    /** 버튼 영역 */
    footer?: ReactNode;
    /** 완료 보고처럼 카드 아래에 덧붙는 것 */
    children?: ReactNode;
    dimmed?: boolean;
};

const CoachOrderCard = ({
    memberName,
    reason,
    facts = [],
    task,
    ownerNote,
    memberDraft,
    draftFallback,
    badge,
    footer,
    children,
    dimmed = false,
}: Props) => {
    const draft = memberDraft?.trim();
    const note = ownerNote?.trim();

    return (
        <div
            className="rounded-[14px]"
            /*
              카드 한 장을 하나로 묶어 둔다. 목록에서는 카드가 여러 장 쌓이고,
              확인 창에서는 왼쪽 본문과 같은 문장이 미리보기에도 뜬다 —
              "이 카드 안의 그것"을 가리킬 수 있어야 한다.
            */
            role="group"
            aria-label={`${memberName} 회원 지시 카드`}
            style={{
                background: '#FFFFFF',
                border: '1px solid #E3EAF3',
                padding: 15,
                opacity: dimmed ? 0.75 : 1,
            }}
        >
            {badge}

            {/* 회원 이름 — 앱 카드의 첫 줄 */}
            <div style={{ marginTop: badge ? 7 : 0 }}>
                <span style={{ fontSize: 16.5, fontWeight: 800, color: '#16202E' }}>
                    {memberName} 회원
                </span>
            </div>

            {/* 왜 이 회원인가. 없으면 그리지 않는다 — 빈 줄이 남으면 근거가 사라진 것처럼 보인다 */}
            {!!reason?.trim() && (
                <div style={{ marginTop: 6 }}>
                    <span style={{ fontSize: 13.5, color: '#2C4C8C', lineHeight: '20px' }}>
                        {reason}
                    </span>
                </div>
            )}

            {/* 근거 수치 — 강사가 회원에게 그대로 말할 수 있는 재료 */}
            {facts.length > 0 && (
                <div
                    className="rounded-[10px] flex flex-col"
                    style={{ marginTop: 10, background: '#F4F6F9', padding: '10px 12px', gap: 4 }}
                >
                    {facts.map((fact) => (
                        <div
                            key={`${fact.label}:${fact.value}`}
                            className="flex items-center justify-between"
                        >
                            <span style={{ fontSize: 12.5, color: '#7C8794' }}>{fact.label}</span>
                            <span style={{ fontSize: 12.5, fontWeight: 700, color: '#2C3A4B' }}>
                                {fact.value}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <div style={{ height: 1, background: '#EDF1F6', margin: '13px 0' }} />

            {/* 무엇을 */}
            <span style={{ fontSize: 14.5, color: '#16202E', lineHeight: '22px', whiteSpace: 'pre-line' }}>
                {task}
            </span>

            {/*
              관리자님 메모 — 원장이 강사에게 한 말. 따옴표까지 앱과 같다.
              회원에게는 가지 않는다 (앱의 draft.ts 는 이 값을 읽지 않는다).
            */}
            {!!note && (
                <div
                    className="rounded-[10px]"
                    style={{ marginTop: 13, background: '#F4F6F9', padding: '10px 12px' }}
                >
                    <span style={{ fontSize: 11.5, color: '#7C8794' }}>관리자님 메모</span>
                    <p
                        style={{
                            fontSize: 13.5,
                            color: '#16202E',
                            marginTop: 5,
                            lineHeight: '20px',
                            whiteSpace: 'pre-line',
                        }}
                    >
                        “{note}”
                    </p>
                </div>
            )}

            {/* 회원에게 건넬 문구 — 강사가 고쳐서 자기 손으로 보낸다 */}
            {draft ? (
                <div
                    className="rounded-[10px]"
                    style={{
                        marginTop: 13,
                        background: '#F7F9FC',
                        borderLeft: '3px solid #3D73DD',
                        padding: '10px 12px',
                    }}
                >
                    <div className="flex items-center justify-between">
                        <span style={{ fontSize: 11.5, fontWeight: 700, color: '#3D73DD' }}>
                            관리자님이 적어준 문구
                        </span>
                        <span style={{ fontSize: 11.5, color: '#3D73DD', textDecoration: 'underline' }}>
                            복사
                        </span>
                    </div>
                    <p
                        style={{
                            fontSize: 13.5,
                            color: '#16202E',
                            marginTop: 7,
                            lineHeight: '20px',
                            whiteSpace: 'pre-line',
                        }}
                    >
                        {draft}
                    </p>
                    <span
                        style={{ fontSize: 11.5, color: '#93A0AE', marginTop: 7, display: 'block' }}
                    >
                        그대로 보내도 되고, 고쳐 쓰셔도 돼요.
                    </span>
                </div>
            ) : (
                <div
                    className="rounded-[10px]"
                    style={{
                        marginTop: 13,
                        background: '#F7F9FC',
                        border: '1px dashed #C9D6E8',
                        padding: '10px 12px',
                    }}
                >
                    {draftFallback}
                </div>
            )}

            {children}
            {footer}
        </div>
    );
};

export default CoachOrderCard;
